import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Camera, Copy, Check, Clock, Link2, MapPin, Mic, Sparkles, Star, Users } from "lucide-react";
import WorkshopPoster from "@/components/WorkshopPoster";
import WorkshopForm, { ConfirmedScreen } from "@/components/WorkshopForm";
import { api } from "@/auth";
import { scrollToId } from "@/i18n";

const EVENT_DATE = new Date("2026-11-21T15:30:00+00:00");

const ease = [0.16, 1, 0.3, 1];

function Countdown() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, EVENT_DATE.getTime() - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff / 3600000) % 24);
  const m = Math.floor((diff / 60000) % 60);
  const s = Math.floor((diff / 1000) % 60);
  const cells = [
    { v: d, l: "días" },
    { v: h, l: "horas" },
    { v: m, l: "min" },
    { v: s, l: "seg" },
  ];
  return (
    <div className="flex gap-3" data-testid="workshop-countdown">
      {cells.map((c) => (
        <div key={c.l} className="min-w-[72px] rounded-xl border border-white/10 bg-card px-4 py-3 text-center">
          <p className="font-display text-2xl font-black text-white sm:text-3xl">{String(c.v).padStart(2, "0")}</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{c.l}</p>
        </div>
      ))}
    </div>
  );
}

const SCHEDULE = [
  { block: "FIRST SESSION", items: [{ t: "15:30 – 17:00", d: "1st Dance Workshop" }, { t: "17:00 – 17:30", d: "Q&A + Individual Photo" }] },
  { block: "BREAK TIME", items: [{ t: "17:30 – 18:00", d: "Break Time" }], accent: true },
  { block: "SECOND SESSION", items: [{ t: "18:00 – 19:30", d: "2nd Dance Workshop" }, { t: "19:30 – 20:00", d: "Q&A + Individual Photo" }] },
];

function ShareButtons() {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Special K-Pop Dance Workshop · JAY KIM", text: "Special K-POP Dance Workshop con JAY KIM · 21 nov 2026 · K-HOUSE Dance Academy", url });
      } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  const whatsapp = `https://wa.me/?text=${encodeURIComponent("Special K-POP Dance Workshop con JAY KIM · 21 nov 2026 · K-HOUSE Dance Academy " + url)}`;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <button data-testid="workshop-share" onClick={share} className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-300 transition-all duration-300 hover:border-accent hover:text-accent">
        {copied ? <Check size={14} /> : <Link2 size={14} />} {copied ? "Enlace copiado" : "Compartir evento"}
      </button>
      <a data-testid="workshop-share-whatsapp" href={whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-300 transition-all duration-300 hover:border-accent hover:text-accent">
        <Camera size={14} /> WhatsApp
      </a>
    </div>
  );
}

export default function Workshop() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preselect = params.get("modalidad");
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/workshop/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);
  const refreshConfig = () => api.get("/workshop/config").then((r) => setConfig(r.data)).catch(() => {});

  useEffect(() => {
    document.title = "SPECIAL K-POP DANCE WORKSHOP · JAY KIM | K-House Dance Academy";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "SPECIAL K-POP DANCE WORKSHOP con JAY KIM (ex-1MILLION, coreógrafo de Jay Park y THE BOYZ). 21 de noviembre de 2026 · K-HOUSE Dance Academy. Reserva tu plaza: Single Workshop 20€ o Full Experience 35€.");
    return () => {
      document.title = "K-house Dance Academy | Curso 2026/2027";
    };
  }, []);

  const goForm = (modality) => {
    const el = document.getElementById("workshop-form-section");
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -76 });
    else el.scrollIntoView({ behavior: "smooth" });
    if (modality) {
      const next = new URLSearchParams(window.location.search);
      next.set("modalidad", modality);
      navigate(`/workshop?${next.toString()}${window.location.hash}`, { replace: true });
    }
  };

  const intro = useMemo(
    () => [
      "Tenemos el placer de anunciar una experiencia de formación muy especial en K-HOUSE Dance Academy.",
      "En esta ocasión contaremos con JAY KIM, antiguo profesor de la prestigiosa academia 1MILLION y coreógrafo de artistas como Jay Park y THE BOYZ.",
      "Una oportunidad única para aprender directamente de un profesional con experiencia en la industria del K-POP, trabajar nuestra técnica, mejorar nuestra expresión corporal y disfrutar de una experiencia de danza diferente.",
      "Un encuentro pensado para bailarines, amantes del K-POP y todas aquellas personas que quieran vivir una experiencia de formación con un profesional del sector.",
    ],
    []
  );

  return (
    <div className="mx-auto max-w-7xl px-5 pb-24 pt-28 sm:px-8">
      <section id="wk-poster">
        <WorkshopPoster />
      </section>

      <section className="mt-16 grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
        <div>
          <motion.p initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.3em] text-accent">
            <Sparkles size={13} className="mr-2 inline" /> An exclusive dance experience
          </motion.p>
          <div className="space-y-4">
            {intro.map((p, i) => (
              <motion.p key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.55, delay: i * 0.07 }} className="text-sm leading-relaxed text-zinc-300 sm:text-base">
                {p}
              </motion.p>
            ))}
          </div>
          <div className="mt-8">
            <Countdown />
          </div>
          <div className="mt-8">
            <ShareButtons />
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7 }}>
          <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight sm:text-4xl">Workshop Schedule</h2>
          <div className="mt-7 space-y-0" data-testid="workshop-schedule">
            {SCHEDULE.map((b, bi) => (
              <div key={b.block} className={`relative border-l-2 ${b.accent ? "border-amber-400/70" : "border-accent/50"} pl-6 ${bi > 0 ? "pt-8" : "pt-2"}`}>
                <span className={`absolute -left-[9px] top-3 h-4 w-4 rounded-full border-2 border-ink ${b.accent ? "bg-amber-400" : "bg-accent"}`} />
                <p className={`font-mono text-[11px] font-bold uppercase tracking-[0.3em] ${b.accent ? "text-amber-300" : "text-accent"}`}>{b.block}</p>
                {b.items.map((it) => (
                  <div key={it.d} className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="flex items-center gap-1.5 font-mono text-sm text-white">
                      <Clock size={13} className="text-zinc-500" /> {it.t}
                    </span>
                    <span className="text-sm text-zinc-300">{it.d}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-xl border border-white/10 bg-card p-5" data-testid="workshop-location">
            <p className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-accent">
              <MapPin size={13} /> Ubicación
            </p>
            <p className="mt-3 text-sm font-bold text-white">Asociación juvenil de baile K-house</p>
            <p className="text-sm text-zinc-400">C. Juan Rumeu García, 8, 38008 Santa Cruz de Tenerife</p>
            <p className="mt-2 flex items-center gap-1.5 text-xs text-zinc-500">
              <Users size={13} /> Aforo: 30 personas por clase
            </p>
            <a
              data-testid="workshop-maps-link"
              href="https://www.google.com/maps/search/?api=1&query=C.+Juan+Rumeu+Garc%C3%ADa+8,+38008+Santa+Cruz+de+Tenerife"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-300 transition-all duration-300 hover:border-accent hover:text-accent"
            >
              <MapPin size={13} /> Cómo llegar · Google Maps
            </a>
          </div>
        </motion.div>
      </section>

      <section className="mt-24">
        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center font-display text-3xl font-extrabold uppercase tracking-tight sm:text-5xl">
          Choose your <span className="text-accent">experience</span>
        </motion.h2>
        <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-6 md:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} className="relative flex flex-col rounded-2xl border-2 border-white/15 bg-card p-7 transition-colors duration-300 hover:border-white/40">
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-zinc-500">Option 01</p>
            <h3 className="mt-2 font-display text-xl font-extrabold uppercase">Single Workshop</h3>
            <p className="mt-4 font-display text-5xl font-black text-white">
              20<span className="text-2xl text-zinc-400">€</span>
            </p>
            <ul className="mt-6 flex-1 space-y-2.5 text-sm text-zinc-300">
              {["Acceso a uno de los dos workshops", "Acceso al Q&A correspondiente", "Fotografía individual con JAY KIM", "Oportunidad de conseguir su autógrafo"].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check size={15} className="mt-0.5 shrink-0 text-accent" /> {f}
                </li>
              ))}
            </ul>
            <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500" data-testid="wk-remaining-single">
              {config
                ? `Plazas libres · 1ª sesión: ${config.remaining_single_first} · 2ª sesión: ${config.remaining_single_second}`
                : "Elige: 1ª sesión (15:30 – 17:00) o 2ª sesión (18:00 – 19:30)"}
            </p>
            <button data-testid="wk-cta-single" onClick={() => goForm("single")} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent px-6 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white shadow-[0_0_25px_rgba(255,0,122,0.4)] transition-all duration-300 hover:bg-accent-hover">
              Book Single Workshop – 20 € <ArrowUpRight size={15} />
            </button>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ delay: 0.1 }} className="relative flex flex-col rounded-2xl border-2 border-accent bg-gradient-to-b from-[#2A0018] to-card p-7 shadow-[0_0_45px_rgba(255,0,122,0.3)] transition-all duration-300 hover:shadow-[0_0_60px_rgba(255,0,122,0.45)]">
            <span className="absolute -top-3 right-5 rounded-full bg-accent px-3.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-white">
              Experiencia completa
            </span>
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-accent">Option 02</p>
            <h3 className="mt-2 font-display text-xl font-extrabold uppercase">Full Experience</h3>
            <p className="mt-4 font-display text-5xl font-black text-accent">
              35<span className="text-2xl text-zinc-400">€</span>
            </p>
            <ul className="mt-6 flex-1 space-y-2.5 text-sm text-zinc-200">
              {["Acceso a los dos workshops", "Acceso a ambos Q&A", "Fotografías individuales", "Oportunidad de conseguir su autógrafo"].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Star size={15} className="mt-0.5 shrink-0 text-accent" /> {f}
                </li>
              ))}
            </ul>
            <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400" data-testid="wk-remaining-full">
              {config ? `Asistencia a las dos sesiones · ${config.remaining_full} plazas libres` : "Asistencia automática a las dos sesiones"}
            </p>
            <button data-testid="wk-cta-full" onClick={() => goForm("full")} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent px-6 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white shadow-[0_0_28px_rgba(255,0,122,0.5)] transition-all duration-300 hover:scale-[1.02] hover:bg-accent-hover">
              Book Full Experience – 35 € <ArrowUpRight size={15} />
            </button>
          </motion.div>
        </div>
        <div className="mx-auto mt-10 max-w-4xl">
          <button data-testid="wk-cta-main" onClick={() => goForm(preselect)} className="w-full rounded-full border-2 border-accent bg-transparent px-8 py-5 font-display text-sm font-extrabold uppercase tracking-[0.2em] text-accent transition-all duration-300 hover:bg-accent hover:text-white sm:text-base">
            Book your place · Reserva tu plaza
          </button>
        </div>
      </section>

      <section className="mt-24">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} className="mx-auto max-w-3xl rounded-2xl border border-white/10 bg-card p-8 sm:p-10">
          <h2 className="flex items-center gap-3 font-display text-2xl font-extrabold uppercase tracking-tight sm:text-3xl">
            <Mic size={22} className="text-accent" /> Q&amp;A — Meet, Photo &amp; Autograph
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-zinc-300 sm:text-base">
            El Q&amp;A no es una simple ronda de preguntas: es un encuentro especial con JAY KIM en el que los participantes podrán hacerle preguntas, interactuar directamente con él, realizarse una fotografía individual y conseguir su autógrafo.
          </p>
          <p className="mt-3 text-sm text-zinc-400">Cada modalidad incluye el encuentro correspondiente a las sesiones contratadas.</p>
        </motion.div>
      </section>

      <section id="workshop-form-section" className="mt-24 scroll-mt-24">
        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10 text-center font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl">
          Book your workshop — <span className="text-accent">Reserva tu plaza</span>
        </motion.h2>
        <WorkshopForm config={config} onRegistered={refreshConfig} />
      </section>
    </div>
  );
}
